import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Typography,
  Space,
  Select,
  message,
  Col,
  Row,
  DatePicker,
} from 'antd';
import moment from 'moment';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  getProjects,
  createProject,
  updateProject,
  deleteProject,
} from '../services/projectsService';
import { getContacts } from '../services/contactsService';
import { getSettings } from '../services/settingsService';

function ProjectsAdmin() {
  const [projects, setProjects] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projectsLabel, setProjectsLabel] = useState('Projects');
  const [form] = Form.useForm();
  const { Title } = Typography;

  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projectsData, contactsData, settingsData] = await Promise.all([
          getProjects(),
          getContacts(),
          getSettings(),
        ]);
        setProjects(projectsData);
        setContacts(contactsData);
        const projectsLabelData = settingsData.find(setting => setting.key === 'projects_title');
        setProjectsLabel(projectsLabelData.value);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const fetchProjects = async () => {
    try {
      const data = await getProjects();
      setProjects(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async id => {
    Modal.confirm({
      title: `Are you sure you want to delete this ${projectsLabel}?`,
      onOk: async () => {
        setLoading(true);
        try {
          await deleteProject(id);
          fetchProjects();
          message.success('Deleted successfully');
        } catch (err) {
          setError(err.message);
          message.error(err.message);
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      sorter: (a, b) => a.title.localeCompare(b.title),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: 'Contacts',
      dataIndex: 'contacts',
      key: 'contacts',
      render: (_, record) =>
        record.project_contacts
          ?.map(pc => `${pc.contact.first_name} ${pc.contact.last_name} (${pc.role})`)
          .join(', '),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => navigate(`/admin/projects/${record.id}`)}
          />
          <Button icon={<DeleteOutlined />} danger onClick={() => handleDelete(record.id)} />
        </Space>
      ),
    },
  ];

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div style={{ padding: '24px' }}>
      <Space style={{ marginBottom: 16 }} direction="vertical" size="middle">
        <Title level={2}>{projectsLabel}</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/admin/projects/new')}
        >
          Add {projectsLabel}
        </Button>
      </Space>

      <Table loading={loading} columns={columns} dataSource={projects} rowKey="id" />
    </div>
  );
}

export default ProjectsAdmin;
